import React from 'react';

import { Dialog, DialogActions, DialogContent, DialogProps, DialogTitle, IconButton } from '@mui/material';

import { CloseButton } from 'components/common/CloseButton/CloseButton';

import styles from './Modal.module.scss';

export interface ModalProps extends Omit<DialogProps, 'title'> {
    /**
     * Whether the modal is open
     */
    open: boolean;
    /**
     * Callback fired when the modal requests to be closed
     */
    onClose?: () => void;
    /**
     * The title of the modal
     */
    title?: React.ReactNode;
    /**
     * Optional icon to display before the title
     */
    titleIcon?: React.ReactNode;
    /**
     * Optional subtitle text below the title
     */
    subtitle?: React.ReactNode;
    /**
     * The content of the modal
     */
    children: React.ReactNode;
    /**
     * Optional footer actions (buttons)
     */
    actions?: React.ReactNode;
    /**
     * Optional left additional footer actions (buttons)
     */
    leftActions?: React.ReactNode;
    /**
     * Whether to show the close button
     */
    showCloseButton?: boolean;
    /**
     * Custom width for the modal
     */
    width?: string | number;
}

/**
 * Custom Modal component based on MUI Dialog
 * Implements design system styling from Figma modal Modal component
 * Supports light and dark themes via CSS variables
 * Features: title with icon, subtitle, content area, action buttons, close button
 */
export const Modal: React.FC<ModalProps> = ({
    open,
    onClose,
    title,
    titleIcon,
    subtitle,
    children,
    leftActions,
    actions,
    showCloseButton = true,
    width,
    ...props
}) => {
    return (
        <Dialog
            open={open}
            onClose={onClose}
            classes={{
                paper: styles.modalPaper,
            }}
            PaperProps={{
                style: width ? { width: typeof width === 'number' ? `${width}px` : width } : undefined,
            }}
            {...props}
        >
            {/* Header */}
            {(title || showCloseButton) && (
                <DialogTitle className={styles.modalHeader}>
                    <div className={styles.headerContent}>
                        {titleIcon && <span className={styles.titleIcon}>{titleIcon}</span>}
                        {title && <span className={styles.title}>{title}</span>}
                    </div>
                    {showCloseButton && <CloseButton onClick={onClose} />}
                </DialogTitle>
            )}

            {/* Subtitle */}
            {subtitle && <div className={styles.subtitle}>{subtitle}</div>}

            {/* Content */}
            <DialogContent className={styles.modalContent}>{children}</DialogContent>

            <div className={styles.modalActionsWrapper}>
                {/* Left actions */}
                {leftActions && <DialogActions className={styles.leftModalActions}>{leftActions}</DialogActions>}
                {/* Actions */}
                {actions && <DialogActions className={styles.modalActions}>{actions}</DialogActions>}
            </div>
        </Dialog>
    );
};

export default Modal;
